import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit, output, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { Toast } from 'ngx-toastr';
import { interval, Observable, of, Subject } from 'rxjs';
import { catchError, filter, takeUntil } from 'rxjs/operators';
import { ToastAction } from '../types/toast-action';

@Component({
  selector: 'app-toast',
  templateUrl: './toast.component.html',
  styleUrls: ['./toast.component.less'],
  animations: [
    trigger('flyInOut', [
      state('inactive', style({ opacity: 0 })),
      state('active', style({ opacity: 1 })),
      state('removed', style({ opacity: 0 })),
      transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
      transition('active => removed', animate('{{ easeTime }}ms {{ easing }}')),
    ]),
  ],
  imports: [MatIcon],
})
export class ToastComponent extends Toast implements OnInit, OnDestroy {
  public readonly countdown = signal(false);
  public readonly duration = signal(0);
  public readonly persistent = signal(false);

  public readonly afterTimeout = output();

  public actions: ToastAction[] = [];
  public icon?: string;
  public readonly counter = signal(0);
  public actionExecuted = false;

  private readonly alive = new Subject<void>();

  public setActions(actions: ToastAction[]) {
    this.actions = actions;
  }

  public setIcon(icon: string | undefined) {
    this.icon = icon;
  }

  protected action(action: ToastAction, event: Event) {
    event.stopPropagation();

    if (this.actionExecuted) {
      return;
    }

    this.actionExecuted = true;

    if (action.handler instanceof Observable) {
      action.handler
        .pipe(
          catchError(() => of(null)),
          takeUntil(this.alive),
        )
        .subscribe(() => {
          this.toastPackage.triggerAction();
          this.remove();
        });
    } else {
      action.handler();
      this.toastPackage.triggerAction();
      this.remove();
    }
  }

  public ngOnInit() {
    this.counter.set(this.duration() / 1000);

    interval(1000)
      .pipe(
        filter(() => !this.actionExecuted && !this.persistent()),
        takeUntil(this.alive),
      )
      .subscribe(() => {
        this.counter.update((v) => v - 1);

        if (this.counter() < 1) {
          this.remove();
        }
      });
  }

  public override ngOnDestroy() {
    !this.actionExecuted && this.afterTimeout.emit();
    this.alive.next();
    this.alive.complete();
  }
}
